import React, { useEffect, useMemo, useState } from "react"
import { connect } from "react-redux"
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, Container, Spinner, Table } from "reactstrap"
import { get, post } from "helpers/api_helper"
import Breadcrumbs from "components/Common/Breadcrumb"
import useLogisticsOperation from "pages/EventsManagement/components/Tabs/Logistics/useLogisticsOperation"
import { submitFailure } from "pages/EventsManagement/components/Tabs/Competitions/competitionOperation"
import ProductDialog from "./ProductDialog"
import ImpactDialog from "./ImpactDialog"
import { FAMILIES, emptyForm, formErrors, formOf, productOf } from "./productForm"
import { familyName } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { expiryLabel, namesLabel, ruleName, ruleParameters, scopeLabel, stockLabel, supplierName, unitLabel } from "./productLabels"
import "pages/EventsManagement/components/Tabs/Logistics/logistics.scss"
import "./products.scss"

const UNCONFIRMED = "The catalogue could not confirm this change. Reload the page before trying again."
const messages = {
  LOGISTICS_REVISION_CONFLICT: "The catalogue or the sheets changed since the impact was shown. Save again to see the current impact.",
  LOGISTICS_PRODUCT_NOT_FOUND: "This product is no longer in the catalogue. Reload the page.",
  LOGISTICS_PRODUCT_EXISTS: "This product was already created. Reload the page.",
  INVALID_LOGISTICS_PRODUCT: "Check the product's fields: a name of up to 120 characters, whole pieces per unit and a package of 2 units or more.",
  INVALID_LOGISTICS_RULE: "Check the rule: a percentage refers to a product with a rule that is not a percentage, and a product that another "
    + "product's percentage refers to keeps such a rule.",
  ERROR_PERMISSIONS: "Only an active SuperAdmin can change the catalogue.",
  SUPER_ADMIN_REQUIRED: "Only an active SuperAdmin can change the catalogue.",
  OPERATION_PAYLOAD_CONFLICT: "This request belongs to a different change. Reload before trying again.",
}
export const catalogueMessage = code => messages[code] || UNCONFIRMED
const SAVED = "Product saved. The change is logged with its values before and after."
// The impact read failed without an answer that says why (a 5xx, production's masked 500, no answer): nothing was sent.
const IMPACT_UNCHECKED = "The impact of this change could not be checked right now. Nothing was saved. Save again."

// Logistics → Products (US 138–143): the catalogue, filtered by family. Saving a product previews its
// impact on the sheets in preparation first; a change that reaches them is applied only after the
// SuperAdmin confirms that impact, and every change is bound to the preview's revision.
export function LogisticsProducts({ settings = {} }) {
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState({ loading: true })
  const [family, setFamily] = useState("all")
  // The product being edited ({ productId, name, form, errors }), and the impact of saving it.
  const [editing, setEditing] = useState(null)
  const [impact, setImpact] = useState(null)
  const [checking, setChecking] = useState(false)
  const [failure, setFailure] = useState("")
  const [notice, setNotice] = useState("")
  const operation = useLogisticsOperation(undefined, catalogueMessage)

  useEffect(() => {
    let current = true
    get("/admin/logistics/products")
      .then(response => current && setState({ products: response?.data?.products || [] }))
      .catch(() => current && setState({ error: "The catalogue could not be loaded." }))
    return () => { current = false }
  }, [attempt])

  const products = useMemo(() => state.products || [], [state.products])
  const shown = family === "all" ? products : products.filter(product => product.family === family)
  const categoryNames = useMemo(() => Object.fromEntries((settings.categories || []).map(({ key, name }) => [key, name])), [settings.categories])
  const pending = checking || operation.pending

  const open = product => {
    setFailure("")
    setNotice("")
    setEditing(product ? { productId: product.id, name: product.name, form: formOf(product) } : { productId: null, name: "", form: emptyForm() })
  }
  const close = () => {
    setEditing(null)
    setImpact(null)
    setFailure("")
  }

  // Sends the change with its preview's id and revision. A stale revision closes the impact, so the
  // next save previews the current one; a result that cannot be confirmed reloads the catalogue.
  const apply = async change => {
    setFailure("")
    const { action, product, preview } = change
    const result = await operation.run(action, "/admin/logistics/products", { action, productId: preview.productId, product, revision: preview.revision })
    if (!result || result.status === "cancelled") return
    if (result.status === "saved") {
      close()
      setNotice(SAVED)
      setAttempt(value => value + 1)
      return
    }
    if (result.code === "LOGISTICS_REVISION_CONFLICT") setImpact(null)
    if (result.status !== "error") setAttempt(value => value + 1)
    setFailure(result.message || UNCONFIRMED)
  }

  const save = async () => {
    const errors = formErrors(editing.form)
    setEditing({ ...editing, errors })
    if (Object.keys(errors).length) return
    const change = { action: editing.productId ? "updateProduct" : "createProduct", ...(editing.productId ? { productId: editing.productId } : {}),
      product: productOf(editing.form) }
    setChecking(true)
    setFailure("")
    try {
      const preview = (await post("/admin/logistics/products/impact", change))?.data
      setChecking(false)
      // The dialog names what the edit changes against the product as the page read it.
      if (preview.sheets.length) setImpact({ ...change, stored: products.find(({ id }) => id === change.productId) || null, preview })
      else await apply({ ...change, preview })
    } catch (error) {
      setChecking(false)
      // The impact read passes the same gateway gates as the change it previews, so it fails like a refused first send.
      setFailure(submitFailure(error, catalogueMessage, true).message || IMPACT_UNCHECKED)
    }
  }

  return <div className="page-content logistics-products">
    <Container fluid>
      <Breadcrumbs title="Logistics" breadcrumbItem="Products" path="/logistics/products" />
      {notice && <Alert color="success" isOpen toggle={() => setNotice("")}>{notice}</Alert>}
      <Card>
        <CardBody>
          <div className="logistics-products-head">
            <ButtonGroup className="logistics-family-filters" aria-label="Family">
              {["all", ...FAMILIES].map(key => <Button key={key} size="sm" color="primary" outline={family !== key} onClick={() => setFamily(key)}>
                {key === "all" ? "All" : familyName(key)}
              </Button>)}
            </ButtonGroup>
            <Button color="primary" size="sm" onClick={() => open(null)} disabled={!state.products}><i className="bx bx-plus mr-1" />Add product</Button>
          </div>
          <p className="small text-muted">Products are deactivated, never deleted. Every change is logged with its values before and after.</p>
          {state.loading && <div className="text-center my-4"><Spinner color="primary" /></div>}
          {state.error && <Alert color="danger" className="mb-0">{state.error}{" "}
            <Button color="link" className="p-0 align-baseline" onClick={() => { setState({ loading: true }); setAttempt(value => value + 1) }}>Retry</Button>
          </Alert>}
          {state.products && (shown.length ? <Table className="logistics-stack mb-0">
            <thead><tr><th>Product</th><th>Rule</th><th>Unit · packaging</th><th>Default supplier</th><th>Stock</th><th>Status</th></tr></thead>
            <tbody>{shown.map(product => <tr key={product.id} className={product.active ? "" : "logistics-inactive"}>
              <td>
                <Button color="link" className="p-0 text-left font-weight-medium" onClick={() => open(product)}>{product.name}</Button>
                <div className="small text-muted">{scopeLabel(product, categoryNames)}</div>
                <div className="small text-muted">{namesLabel(product)}</div>
              </td>
              <td data-label="Rule">
                <div className={product.rule ? "" : "text-muted"}>{ruleName(product.rule, product)}</div>
                {ruleParameters(product, products) && <div className="small text-muted">{ruleParameters(product, products)}</div>}
              </td>
              <td data-label="Unit · packaging">
                <div>{unitLabel(product)}</div>
                {product.grouping && <div className="small text-muted">{`${product.grouping.name} of ${product.grouping.units}`}</div>}
                <div className="small text-muted">{product.unitWeight != null ? `${product.unitWeight} kg / unit` : "weight not set"}</div>
              </td>
              <td data-label="Default supplier">
                <Badge className={product.supplier === "mpg" ? "badge-soft-primary" : "badge-soft-secondary"}>{supplierName(product.supplier)}</Badge>
              </td>
              <td data-label="Stock">
                <div className={product.minimum != null ? "" : "text-muted"}>{stockLabel(product)}</div>
                {product.expiresOn && <div className="small text-muted">{expiryLabel(product.expiresOn)}</div>}
              </td>
              <td data-label="Status">
                <Badge className={product.active ? "badge-soft-success" : "badge-soft-secondary"}>{product.active ? "Active" : "Inactive"}</Badge>
              </td>
            </tr>)}</tbody>
          </Table> : <p className="text-muted mb-0">No products in this family.</p>)}
        </CardBody>
      </Card>
    </Container>
    {editing && !impact && <ProductDialog editing={editing} products={products} settings={settings} pending={pending} failure={failure}
      onChange={form => setEditing({ ...editing, form })} onSave={save} onClose={close} />}
    {impact && <ImpactDialog impact={impact} pending={pending} failure={failure} onConfirm={() => apply(impact)}
      onCancel={() => { setImpact(null); setFailure("") }} />}
  </div>
}

export default connect(({ session: { settings } = {} }) => ({ settings: settings || {} }))(LogisticsProducts)
